import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import MarketingMoodboardMiddleSchool from './MarketingMoodboardMiddleSchool';
import Button from '../components/Button';
import LoadingBar from '../components/LoadingBar';
import Tooltip from '../components/Tooltip';
import SpinnerOverlay from '../SpinnerOverlay';
import ModalContainer from '../components/ModalContainer';
import StudentRow from './StudentRow';
import SingleUnit from './SingleUnit';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import { isUserHasAnyKeys } from '../GlobalFunctions';
import { getJob, listMultipleGroupAssignmentsByModule, getMiddleSchoolModule } from '../BackendInterface';
import { InlineIcon } from '@iconify/react';
import SortDropdown from '../components/SortDropdown';
import circleCheck from '@iconify/icons-akar-icons/circle-check';
import banIcon from '@iconify-icons/ion/ban';
import styles from './StrengthsReport.module.scss';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import { Assignment, Module } from '@peekapak/types/LessonPlanTypes';
import { getHighSchoolUnits } from '../components/LibraryCard/cardData';
import PrimaryButton from '../components/PrimaryButton';
import { getStrengthsModuleIds } from '../StudentStrengths/strengthsHelpers';

interface ClassroomProps {
  classroomProps?: ClassroomType;
}

interface StudentData {
  firstName: string;
  lastName?: string;
  userId: string;
  assignments: Assignment[];
}

const StrengthsReport = ({ classroomProps }: ClassroomProps): JSX.Element => {
  const [moduleData, setModuleData] = useState<Module[]>([]);
  const [isShowStudents, setShowStudents] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isShowWheelModal, setShowModal] = useState(false);
  const [downloadCompletion, setDownloadCompletion] = useState(0);
  const [displayUnit, setDisplayUnit] = useState(1);
  const [classData, setClassData] = useState<StudentData[]>([]);

  const isSmall = useMediaQuery({ maxWidth: 1080 });

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = classroomProps ? classroomProps : selectClassroom(state);
    return selectedClassroom;
  });

  const dropdownOptions = [
    { text: 'All Units', value: 1, unitId: '' },
    { text: 'Self-Awareness', value: 2, unitId: 'hsidentity' },
    { text: 'Stress + Regulation', value: 3, unitId: 'hsstress' },
    { text: 'Decision Making', value: 4, unitId: 'hsdecision' },
    { text: 'Belonging', value: 5, unitId: 'hsbelonging' },
    { text: 'Conflict Resolution', value: 6, unitId: 'hsempathy' },
  ];

  const handleDropdownChange = (e: React.FormEvent<HTMLSelectElement>) => {
    setDisplayUnit(parseInt(e.currentTarget.value));
  };

  const isLocked = !isUserHasAnyKeys(['KEY_MOODBOARD', 'KEY_PEEKAPAK_MANAGEMENT', 'KEY_WIP', 'KEY_MASTER_KEY']);

  const handleToggleNames = () => {
    setShowStudents((prevState) => !prevState);
  };

  const hideModal = () => setShowModal((prevState) => !prevState);

  const getStrengthsData = async () => {
    function isJobPending(job: { status: string }) {
      return job.status !== 'Done' && job.status !== 'Error';
    }
    function processPendingJob(job: { data: string }) {
      if (job.data) {
        const data = JSON.parse(job.data);
        setDownloadCompletion(data.completionPercent);
      }
    }

    function yieldAndWait() {
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
    }
    const moduleIds = getStrengthsModuleIds();
    const jobId = await listMultipleGroupAssignmentsByModule(classroom.classroomId, moduleIds);
    let job: {
      status: string;
      data: any;
    } = await getJob(jobId.jobId);

    try {
      while (isJobPending(job)) {
        processPendingJob(job);
        await yieldAndWait();
        job = await getJob(jobId.jobId);
      }
    } catch (error) {
      console.error(error);
    }

    const data = JSON.parse(job.data);

    if (job.status === 'Done') {
      setDownloadCompletion(100);
      const alphabeticalData = data.sort((a: StudentData, b: StudentData) =>
        (a.lastName ?? a.firstName).localeCompare(b.lastName ?? b.firstName),
      );
      setClassData(alphabeticalData);
      return;
    } else if (job.status === 'Error') {
      console.error(job.data);
    } else {
      throw new Error(`Unexpected job status ${job.status}`);
    }
  };

  const loadStudentData = useCallback(async () => {
    try {
      classroom && getStrengthsData();
    } catch {
      console.error('Error loading student data');
    }
  }, [classroom]);

  const loadModuleData = useCallback(async () => {
    try {
      const moduleIds = getStrengthsModuleIds();
      const moduleData = await Promise.all(
        moduleIds.map(async (moduleId) => {
          const thisModule = await getMiddleSchoolModule(moduleId, classroom.language, classroom.grade.toString());
          return thisModule.data as unknown as Module;
        }),
      );
      setModuleData(moduleData);
    } finally {
      setIsLoaded(true);
    }
  }, [classroom]);

  const handleClassChange = useCallback(() => {
    setClassData([]);
    setModuleData([]);
    setDisplayUnit(1);
    setDownloadCompletion(0);
    setIsLoaded(false);
    setShowStudents(true);
    loadStudentData();
  }, [classroom]);

  useEffect(() => {
    handleClassChange();
  }, [classroom]);

  useEffect(() => {
    classroom && loadModuleData();
  }, [classroom]);

  return isLocked ? (
    <MarketingMoodboardMiddleSchool key='noReports' />
  ) : (
    <div className={classroomProps ? styles.embedContainer : styles.container} key='hasAccess'>
      <SpinnerOverlay isShow={!isLoaded} />
      <div className={styles.titleBar}>
        <h2 className={styles.reportTitle}>Student Strengths</h2>
        {displayUnit !== 1 && !isSmall ? (
          <span className={styles.statusLegend}>
            <InlineIcon icon={circleCheck} width={24} color='#00ca48' />
            <p>Submitted</p>
            <span className={styles.circleOutline} />
            <p>In Progress</p>
            <h4>-</h4>
            <p>Not Started</p>
          </span>
        ) : (
          !isSmall && (
            <span className={styles.helperText} onClick={() => setShowModal(true)}>
              <Tooltip
                content='More about this report'
                direction='top'
                arrowPosition='left-align'
                color='white'
                maxWidth='20em'
              >
                What is this?
              </Tooltip>
              {isShowWheelModal && (
                <ModalContainer hideModal={hideModal}>
                  <div className={styles.modalContainer}>
                    <button className={styles.xButton} onClick={hideModal}>
                      x
                    </button>
                    <h3>What Are Strengths Reports?</h3>
                    <p>
                      Students in grades 9-12 begin each unit with a SELf Assessment, a reflection that identifies their
                      unique strength and areas where they can find balance.
                    </p>
                    <p>
                      Using the filter dropdown in the top right corner, you can drill down into each unit, allowing you
                      to view in detail what each skill means.
                    </p>
                    <div className={styles.buttonRow}>
                      <PrimaryButton small onClick={hideModal}>
                        Got it!
                      </PrimaryButton>
                    </div>
                  </div>
                </ModalContainer>
              )}
            </span>
          )
        )}
        <div className={styles.rightButtons}>
          <SortDropdown
            options={dropdownOptions}
            onChange={handleDropdownChange}
            value={displayUnit}
            styleGroup='reports'
          />
          <Button small darkBlue thinText onClick={handleToggleNames}>
            {isShowStudents ? (
              <div className={styles.buttonText} key={'hide student button'}>
                Hide Student Info&nbsp;&nbsp;
                <InlineIcon icon={banIcon} width={24} />
              </div>
            ) : (
              <div className={styles.buttonText} key={'show student button'}>
                View Student Info&nbsp;&nbsp;
                <InlineIcon icon={circleCheck} width={24} />
              </div>
            )}
          </Button>
        </div>
      </div>
      {downloadCompletion < 100 ? (
        <div className={styles.loadingBarContainer} key='loadingBarContainer'>
          <LoadingBar
            title='Downloading Student Data'
            width={300}
            numComplete={downloadCompletion}
            totalNum={100}
            barColor='#012a73'
          />
        </div>
      ) : classData.length === 0 ? (
        <div className={styles.loadingBarContaier}>
          <h3>No data available</h3>
          <p>This report will be available once students have completed the Strengths Assessment.</p>
        </div>
      ) : displayUnit === 1 ? (
        <div className={styles.allUnitContainer}>
          <div className={styles.titleRow}>
            <div className={styles.nameCell}>
              <h4>{!isSmall ? 'Student Name' : 'Name'}</h4>
            </div>
            <div className={styles.unitTitles}>
              {dropdownOptions.slice(1).map((unit) => (
                <div className={styles.titleCell} key={unit.unitId}>
                  <div className={styles.unitTitle}>
                    <h4>{unit.text}</h4>
                    {!isSmall && <p>SELf Assessment</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {classData.length > 0 &&
            moduleData.length > 0 &&
            classData?.map((student) => {
              const sortedAssignments = dropdownOptions
                .slice(1)
                .map((unit) => student.assignments?.find((assignment) => assignment.unit.includes(unit.unitId)) ?? {});
              return (
                <StudentRow
                  userId={student.userId}
                  data={moduleData}
                  assignments={sortedAssignments}
                  firstName={student.firstName ?? ''}
                  lastName={student.lastName ?? ''}
                  isShowStudents={isShowStudents}
                  key={`${student.userId}`}
                />
              );
            })}
        </div>
      ) : (
        <SingleUnit
          classData={classData}
          moduleData={moduleData}
          isShowStudents={isShowStudents}
          dropdownOptions={dropdownOptions}
          isSmall={isSmall}
          displayUnit={displayUnit}
        />
      )}
    </div>
  );
};

export default StrengthsReport;
